import React, { memo, useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
} from "chart.js";
import {
  BoxPlotController,
  BoxAndWiskers,
} from "@sgratzl/chartjs-chart-boxplot";
import { ViolinController, Violin } from "@sgratzl/chartjs-chart-boxplot";
import { Box, Container, Modal, Typography } from "@mui/material";
import { StudentAnswersCount } from "../../types/queries";
import styles from "../../styles/Chart.module.scss";
import BarChartIcon from "@mui/icons-material/BarChart";
// Register necessary components
ChartJS.register(
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  BoxPlotController,
  BoxAndWiskers,
  ViolinController,
  Violin
);

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const BoxPlotChart = memo(
  ({
    type,
    title,
    data,
    endDate,
  }: {
    type: string;
    title: string;
    data: {
      datasets: {
        label: string;
        data: any[];
        backgroundColor: string[];
        borderColor: string[];
        borderWidth: number;
      }[];
    };
    endDate?: string;
  }) => {
    const chartRef = useRef<HTMLCanvasElement | null>(null);
    const chartInstanceRef = useRef<ChartJS | null>(null);

    // const [open, setOpen] = useState(false);
    // const [selectedUsers, setSelectedUsers] = useState<{
    //   date: string;
    //   users: string[];
    // }>({ date: "", users: [] });

    // const handleOpen = () => setOpen(true);
    // const handleClose = () => setOpen(false);

    console.log("Widget", data.datasets[0].data);

    const inputData: any[] = Array.isArray(data.datasets[0].data)
      ? data.datasets[0].data
      : [];

    // const transformedData: { [date: string]: StudentAnswersCount[] } =
    //   inputData.reduce((acc, entry) => {
    //     if (!entry.bucket_start || !Array.isArray(entry.users)) return acc;

    //     const dateObj = new Date(entry.bucket_start);
    //     if (isNaN(dateObj.getTime())) return acc;

    //     const day = String(dateObj.getDate()).padStart(2, "0");
    //     const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    //     const year = dateObj.getFullYear();

    //     const formattedDate = `${day}-${month}-${year}`;

    //     acc[formattedDate] = entry.users.map(
    //       ({ user, count }: { user: string; count: number }) => ({
    //         user,
    //         count,
    //       })
    //     );
    //     return acc;
    //   }, {} as Record<string, { user: string; count: number }[]>);

    const transformedData: Record<string, { user: string; count: number }[]> =
      {};

    const sortedData = [...inputData].sort(
      (a, b) =>
        new Date(a.bucket_start).getTime() - new Date(b.bucket_start).getTime()
    );

    for (let i = 0; i < sortedData.length; i++) {
      const current = sortedData[i];
      const next = sortedData[i + 1]; // may be undefined

      if (!current.bucket_start || !Array.isArray(current.users)) continue;

      const start = new Date(current.bucket_start);
      // For the last bucket, use the filter's end date or today's date
      const end = next?.bucket_start
        ? new Date(next.bucket_start)
        : endDate
        ? new Date(endDate.split("/").reverse().join("-")) // Convert DD/MM/YYYY to YYYY-MM-DD
        : new Date(); // Fallback to current date

      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;

      const format = (d: Date) =>
        `${String(d.getDate()).padStart(2, "0")}/${String(
          d.getMonth() + 1
        ).padStart(2, "0")}`;

      const rangeLabel = `${format(start)} - ${format(end)}`;

      transformedData[rangeLabel] = current.users.map(
        ({ user, count }: { user: string; count: number }) => ({ user, count })
      );
    }

    console.log("Widget 2", transformedData);
    const labels = Object.keys(transformedData);
    console.log(
      "Widget 3",
      Object.values(transformedData).map((u) => u.map((a) => a.count))
    );

    const uniqueUsers = new Set(
      Object.values(transformedData).flatMap((arr) => arr.map((u) => u.user))
    );
    const studentCount = uniqueUsers.size;

    useEffect(() => {
      if (chartRef.current) {
        const ctx = chartRef.current.getContext("2d");
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }

        if (!ctx) return;

        chartInstanceRef.current = new ChartJS(ctx, {
          type: "boxplot",
          data: {
            labels,
            datasets: [
              {
                label: "Student Answer Distribution Within Each Interval",
                type: "boxplot", //"violin",
                backgroundColor: "rgba(195, 157, 251, 0.2)",
                borderColor: "#c5a1fb",
                borderWidth: 1,
                data: Object.values(transformedData).map((u) =>
                  u.map((a) => a.count)
                ),
              },
              {
                label: "Student Answer Counts",
                type: "scatter",
                backgroundColor: "#8763ff",
                data: Object.entries(transformedData).flatMap(
                  ([date, count], dateIndex) =>
                    count.map((entry) => ({
                      x: labels[dateIndex],
                      y: entry.count,
                      user: entry.user,
                    }))
                ),
                pointStyle: "rect",
                radius: 10,
                hoverRadius: 10,
              } as any,
            ],
          },
          options: {
            responsive: true,
            layout: { padding: { bottom: 20 } },
            onClick: (event) => {
              if (!chartInstanceRef.current) return;
              console.log({ event });
              // Get canvas-relative click position
              const chart = chartInstanceRef.current;
              const xAxis = chart.scales["x"];

              if (!event || !event.native) return;
              const clickX = (event.native as PointerEvent).offsetX;

              // Find the closest label (date) based on click position
              let closestLabel = null;
              let closestDistance = Infinity;

              xAxis.ticks.forEach((tick, index) => {
                const tickX = xAxis.getPixelForTick(index);
                const distance = Math.abs(tickX - clickX);
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestLabel = labels[index];
                }
              });

              if (closestLabel && transformedData[closestLabel]) {
                const users = transformedData[closestLabel];
                if (!users) return;
                // setSelectedUsers({
                //   date: closestLabel,
                //   users: (users as any[]).map((u) => `${u.user}: ${u.answer}`),
                // });
                // handleOpen();
              }
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: function (context) {
                    const dataset = context.chart.data.datasets[
                      context.datasetIndex
                    ] as any;

                    if (dataset.type === "scatter") {
                      const point = context.raw as {
                        x: string;
                        y: number;
                        user: string;
                      };
                      return `${point.user}: Answered ${point.y}`;
                    }

                    // Let Chart.js handle the default for boxplots by returning undefined
                    return undefined;
                  },
                },
              },
              title: {
                display: true,
                // text: title + ` (Total: ${studentCount} students)`,
                text: title,
              },
            },
            // scales: {
            //   y: {
            //     min: 1,
            //     ticks: {
            //       stepSize: 1,
            //     },
            //   },
            // },
          },
          plugins: [
            {
              id: "nPerDateLabel",
              afterDraw: (chart) => {
                const {
                  ctx,
                  chartArea: { bottom },
                  scales: { x },
                } = chart;

                ctx.save();
                ctx.font = "14px sans-serif";
                ctx.fillStyle = "#555";
                ctx.textAlign = "center";
                ctx.textBaseline = "top";

                labels.forEach((label, index) => {
                  const tickX = x.getPixelForTick(index);
                  const studentsForDate = transformedData[label] || [];
                  const uniqueUserCount = new Set(
                    studentsForDate.map((entry) => entry.user)
                  ).size;

                  ctx.fillText(`N = ${uniqueUserCount}`, tickX, bottom + 30);
                });

                ctx.restore();
              },
            },
          ],
        });
      }

      return () => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }
      };
    }, [inputData]);

    return (
      <Container maxWidth={false}>
        {transformedData &&
        Object.values(transformedData).some((arr) => arr.length > 0) ? (
          <canvas
            id='myChart'
            style={{ minHeight: "300px", maxHeight: "700px", padding: 2 }}
            ref={chartRef}
          ></canvas>
        ) : (
          <Box
            minHeight='500px'
            maxHeight='700px'
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            bgcolor='#f9f9f9'
            borderRadius={2}
            margin={2}
            p={2}
          >
            <Typography
              variant='h6'
              fontWeight={"bold"}
              color='textSecondary'
              mb={12}
            >
              Questionnaire Answers by Students Over Time Intervals
            </Typography>
            <BarChartIcon sx={{ fontSize: 100, color: "#ccc", mb: 2 }} />
            <Typography variant='h6' color='textSecondary'>
              No data available for the selected filters.
            </Typography>
          </Box>
        )}

        {/* <Modal open={open} onClose={handleClose}>
          <Box sx={style}>
            <Typography variant='h6' component='h2'>
              {selectedUsers.date}
            </Typography> 
            <ul>
              {selectedUsers.users.map((user, index) => (
                <li key={index}>{user}</li>
              ))}
            </ul>
          </Box>
        </Modal> */}
      </Container>
    );
  }
);

export default BoxPlotChart;

